import { Link } from 'react-router-dom'
import { formatMoney } from '../../lib/format'
import { dayLabel, routeFor } from '../../lib/weekly'
import { COMPETITOR_THREAT, type EvidenceLine, type MovementItem } from '../../types/weekly'

// One thing that moved, with the evidence it rests on: the callout names what happened (stage up, proposal out,
// gone quiet, decision maker reached, tactic worked); the lines underneath are the thread, the signals, the
// meeting, the play that say so. The key on the right is what the narrative cites.
const KIND_WORD: Record<EvidenceLine['kind'], string> = { thread: 'thread', signal: 'signal', meeting: 'meeting', contact: 'contact', tactic: 'play', account: 'account' }

export function MovementCard({ item, cited }: { item: MovementItem; cited: boolean }) {
  const kind = item.slice === 'pipeline' ? 'pursuit' : item.slice === 'accounts' ? 'account' : item.slice === 'people' ? 'person' : item.slice === 'meetings' ? 'meeting' : 'tactic'
  const secondary = item.tags.filter((t) => t !== item.callout)
  const moved = item.stageFrom && item.stageTo && item.stageFrom !== item.stageTo
  // a rival at one of our accounts is not movement: the card says so in the one colour reserved for it
  const threat = item.callout === COMPETITOR_THREAT
  return (
    <article className={`card movement-card tone-${item.tone}${threat ? ' threat' : ''}`}>
      <div className="movement-head">
        <div className="movement-title">
          <span className={`tag callout ${item.tone}${threat ? ' threat' : ''}`}>{item.callout}</span>
          <h3><Link to={routeFor(kind, item.id)}>{item.title}</Link></h3>
          {item.subtitle && <p className="muted small">{item.subtitle}</p>}
        </div>
        <div className="movement-figures">
          {moved && <span className="stage-move"><span className={`tag stage ${item.stageFrom}`}>{item.stageFrom}</span><span className="arrow" aria-hidden="true">to</span><span className={`tag stage ${item.stageTo}`}>{item.stageTo}</span></span>}
          {!moved && item.stageTo && item.slice === 'pipeline' && <span className={`tag stage ${item.stageTo}`}>{item.stageTo}</span>}
          {item.amount != null && item.amount !== 0 && <strong className="movement-amount">{item.slice === 'accounts' && item.amount > 0 ? '+' : ''}{formatMoney(item.amount)}</strong>}
          <span className={`fact-key ${cited ? 'cited' : ''}`} title={cited ? 'Cited by the narrative' : 'Not cited by the narrative'}>{item.key}</span>
        </div>
      </div>
      {secondary.length > 0 && <p className="movement-tags">{secondary.map((t) => <span key={t} className="chip">{t}</span>)}</p>}
      <ul className="evidence">
        {item.evidence.map((e, i) => (
          <li key={`${e.kind}-${e.id}-${i}`} className={`evidence-${e.kind}`}>
            <span className="evidence-kind">{KIND_WORD[e.kind]}</span>
            <span className="evidence-label">{e.label}{e.detail && <span className="muted small evidence-detail">{e.detail}</span>}</span>
            {e.on && <span className="evidence-on">{dayLabel(e.on)}</span>}
          </li>
        ))}
      </ul>
    </article>
  )
}
